/**
 * App shell for authenticated routes.
 *
 * v6 scope (2026-05-18): Operational (Boards), Files, and (M1.7) Company
 * contours are active. Company is admin-only — visible iff the current
 * user has founder/admin in the active org or system_role=founder.
 *
 * Sidebar collapse state persists to localStorage.
 *
 * Mobile PWA polish (2026-05-20):
 *   - Below `md` (768px), the left rail (ActivityBar + Sidebar) is hidden
 *     by default and rendered inside an off-canvas drawer that slides
 *     in from the left. A burger trigger lives in <ScopeBar>.
 *   - BottomNav (iOS-style tab bar) is the primary contour switcher on
 *     mobile; it stays visible across every route.
 *   - The drawer is controlled via React context so any descendant
 *     (e.g. the burger button in <ScopeBar>) can open it without
 *     prop-drilling.
 *
 * Mobile chrome is SHELL-LEVEL, not route-level (2026-08-01, QA D-1/D-2/D-3).
 * <ScopeBar> used to be mounted by two route files — `/board/$boardId` and
 * `/inbox` — and nothing else. Measured on the production bundle at 390x844:
 * `/brain`, `/settings-v2` (every tab), `/company/*`, `/comms` and `/files`
 * rendered NO top bar, therefore no burger, therefore an unopenable drawer:
 * no Workspace switcher and no account menu. A second person who opened
 * Settings on a phone could not switch Workspace and could not sign out
 * without first navigating back to a board. The bar and the account footer are
 * mounted here now, so every authenticated route has them.
 */
import { useLocation, useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import * as React from 'react';


import { useCanAdminOrg } from '../../lib/active-org';
import { useIsMobile } from '../../lib/use-media-query';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { ActivityBar, type ActivityContour } from './activity-bar';
import { BottomNav } from './bottom-nav';
import { MobileAccountFooter } from './mobile-account-footer';
import { MobileDrawerContext } from './mobile-drawer-context';
import { ScopeBar } from './scope-bar';
import { ClaudeAuthBanner } from '../banner/ClaudeAuthBanner';
// "You're in, but no agent will start until you connect your own AI sign-in."
// Shell-level for the same reason ScopeBar is (QA D-1/D-2/D-3): a new person's
// first minutes are spent on whatever route the invitation dropped them on,
// and a notice mounted by one route is a notice most arrivals never see.
// Renders null for anybody who already has a login — which is every user of
// this instance today.
import { ExecutionLaneBanner } from '../banner/ExecutionLaneBanner';
import { LegacyBrowserBanner } from '../banner/LegacyBrowserBanner';


// 2026-06 (Explorer dup-column removal, option A): the /files contour renders
// no shell-side Files sidebar; the Explorer page owns the only tree, full-width.

// Settings V2 (permanent since 2026-07-04) — lazy-loaded to keep the
// Company-grouped tree / profile menu / settings-area code in its own chunk.
const CompanyTree = React.lazy(() =>
  import('./company-tree').then((m) => ({ default: m.CompanyTree })),
);

const COLLAPSE_STORAGE_KEY = 'chaos-sidebar-collapsed';
const CONTOUR_STORAGE_KEY = 'chaos-active-contour';

function readCollapsed(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(value));
  } catch {
    /* ignore */
  }
}

function readContour(): ActivityContour {
  try {
    if (typeof localStorage === 'undefined') return 'operational';
    const raw = localStorage.getItem(CONTOUR_STORAGE_KEY);
    if (
      raw === 'operational' ||
      raw === 'files' ||
      raw === 'brain' ||
      raw === 'company' ||
      raw === 'inbox'
    ) {
      return raw;
    }
    return 'operational';
  } catch {
    return 'operational';
  }
}

function writeContour(value: ActivityContour): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CONTOUR_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

// ───────────────────────── Mobile drawer context ─────────────────────────
//
// The context itself moved to ./mobile-drawer-context (2026-08-01): <Shell>
// mounts <ScopeBar>, and <ScopeBar> consumes the drawer handle, so keeping the
// context here would make the two modules a cycle. Re-exported so existing
// `import { useMobileDrawer } from './shell'` call sites keep working.
export { useMobileDrawer } from './mobile-drawer-context';

// ─────────────────── Collapsed boards-column re-expand edge ───────────────────

// Settings-V2 only (2026-06-18, founder spec): with the chevrons removed, a
// collapsed boards column is re-opened by dragging the rail's right edge
// OUTWARD. This thin handle sits flush against the ActivityBar's right edge
// (the only thing visible when collapsed) and fires `onExpand` once the user
// has dragged far enough to the right to clearly signal intent (≥24px). It
// also re-expands on a plain click/Enter so the affordance is keyboard- and
// mouse-friendly, not drag-only.
const EXPAND_DRAG_THRESHOLD = 24;

function SidebarExpandEdge({ onExpand }: { onExpand: () => void }) {
  const startXRef = React.useRef<number | null>(null);
  const firedRef = React.useRef(false);

  const onMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      startXRef.current = event.clientX;
      firedRef.current = false;

      const onMove = (e: MouseEvent) => {
        if (startXRef.current === null || firedRef.current) return;
        if (e.clientX - startXRef.current >= EXPAND_DRAG_THRESHOLD) {
          firedRef.current = true;
          onExpand();
        }
      };
      const onUp = () => {
        startXRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = prevUserSelect;
        document.body.style.cursor = prevCursor;
      };

      const prevUserSelect = document.body.style.userSelect;
      const prevCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [onExpand],
  );

  return (
    <button
      type="button"
      aria-label="Open boards column"
      title="Open boards"
      onMouseDown={onMouseDown}
      onClick={() => {
        // A plain click (no drag) still opens — keeps the affordance usable
        // without forcing a drag gesture. `firedRef` prevents a double-fire
        // when a drag already expanded it on this same press.
        if (!firedRef.current) onExpand();
      }}
      className="absolute left-12 top-0 z-20 h-full w-1.5 cursor-col-resize border-0 bg-transparent p-0 transition-colors hover:bg-foreground/10"
    />
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => readCollapsed());
  const [contour, setContourState] = React.useState<ActivityContour>(() => readContour());
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const canAdminOrg = useCanAdminOrg();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  // 2026-06-11: CEO chat moved to the bottom of the ActivityBar rail;
  // the rail needs the founder user id to scope the chat's WS channel +
  // localStorage (same id the old ScopeBar CeoChat used).
  const ceoUserId = useAuthStore((s) => s.user?.id ?? null);

  // Nav-area detection (2026-06-21 bugfix). The Settings-V2 area
  // (/settings-v2) doesn't map to a rail contour, so the URL→contour effect
  // falls back to 'operational'. That made the board-picker CompanyTree LEAK
  // into Settings and made the Boards rail icon merely toggle the (hidden)
  // column instead of leaving Settings. We derive the real area from the
  // pathname instead of trusting `contour`:
  //   • inSettings   → hide the board-picker tree entirely.
  //   • onBoardsArea → the Boards icon toggles the column (the existing
  //     mechanic); off the boards area it NAVIGATES to the last board.
  // `/settings/seller` is the one surviving /settings/* screen that renders
  // itself instead of redirecting into settings-v2, so it has to be named
  // here too — otherwise the URL falls through to the 'operational' contour
  // and the board-picker tree leaks in beside it. Measured in the browser on
  // 2026-08-03: the Selling page rendered with an empty board tree next to it.
  const inSettings =
    location.pathname.startsWith('/settings-v2') ||
    location.pathname.startsWith('/settings/seller');
  const onBoardsArea = location.pathname.startsWith('/board'); // /board/:id and /boards

  // Mirror URL → contour so deep links land on the correct sidebar variant.
  React.useEffect(() => {
    const path = location.pathname;
    let derived: ActivityContour;
    // Settings-V2 adds Inbox as a first-class rail contour, so /inbox deep
    // links highlight it.
    if (path.startsWith('/inbox')) derived = 'inbox';
    else if (path.startsWith('/files')) derived = 'files';
    else if (path.startsWith('/brain')) derived = 'brain';
    else if (path.startsWith('/company')) derived = 'company';
    // Comms deep links highlight the Communication Center rail contour. The
    // /comms route itself is flag-gated (redirects Home when VITE_COMM_CENTER
    // is OFF), so this branch is only reachable on an ON build.
    else if (path.startsWith('/comms')) derived = 'comms';
    // Marketplace deep links highlight the Market rail contour. /market is
    // flag-gated in its own beforeLoad (redirects Home when VITE_MARKETPLACE is
    // OFF), so this branch is only reachable on an ON build.
    else if (path.startsWith('/market')) derived = 'market';
    // University deep links highlight the learning rail contour. Unlike
    // /market this branch is reachable on a flag-OFF build too, because the
    // University area has no layout route to redirect from — the rail simply
    // has no icon to light up, and the URL still has to resolve to SOME
    // contour. 'learning' is the honest one.
    else if (path.startsWith('/university')) derived = 'learning';
    else derived = 'operational';
    if (derived !== contour) {
      setContourState(derived);
      writeContour(derived);
    }
  }, [location.pathname, contour]);

  // Auto-close mobile drawer on route change (feels like every native app).
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const toggle = React.useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      writeCollapsed(next);
      return next;
    });
  }, []);

  // Explicit collapse setter (settings-v2): used by the boards-column
  // resize handle (overdrag-past-min snap-collapses) and the collapsed
  // rail-edge handle (drag-out re-expands). Persists like `toggle`.
  const setCollapsedPersist = React.useCallback((next: boolean) => {
    setCollapsed((c) => {
      if (c === next) return c;
      writeCollapsed(next);
      return next;
    });
  }, []);

  const setContour = React.useCallback(
    (c: ActivityContour) => {
      setContourState(c);
      writeContour(c);
      if (c === 'inbox') {
        void navigate({ to: '/inbox' });
      } else if (c === 'files') {
        void navigate({ to: '/files' });
      } else if (c === 'brain') {
        void navigate({ to: '/brain' });
      } else if (c === 'company') {
        void navigate({ to: '/company/org-chart' });
      } else if (c === 'comms') {
        void navigate({ to: '/comms' });
      } else if (c === 'market') {
        void navigate({ to: '/market' });
      } else if (c === 'learning') {
        void navigate({ to: '/university' });
      } else {
        void navigate({ to: '/boards' });
      }
    },
    [navigate],
  );

  // Settings-V2 only: the Boards/'operational' rail icon.
  // Behaviour (founder spec 2026-06-18, corrected 2026-06-21):
  //   • NOT on the Boards area (e.g. in Settings) → NAVIGATE to Boards and
  //     open the last-viewed board (setContour('operational') → /boards,
  //     which resolves the last boardId from localStorage, else the top
  //     board). It does NOT toggle the column — and we make sure the column
  //     is open on arrival.
  //   • ALREADY on the Boards area → re-tap TOGGLES the column open/closed
  //     (the existing collapse mechanic).
  // Keyed off the real pathname (`onBoardsArea`), not `contour`, because the
  // Settings area has no contour of its own and falls back to 'operational'.
  const onContourSelect = React.useCallback(
    (c: ActivityContour) => {
      if (c === 'operational') {
        if (onBoardsArea) {
          // Already on the Boards area → re-tap toggles the column.
          toggle();
          return;
        }
        // Coming from elsewhere (Settings, Brain, Company, …) → open the
        // column and let setContour navigate to /boards, which resolves the
        // last-viewed board (else the top board).
        setCollapsedPersist(false);
      }
      setContour(c);
    },
    [onBoardsArea, toggle, setCollapsedPersist, setContour],
  );

  // Render the appropriate left rail. Company and Brain don't have their own
  // sidebar — their secondary nav lives inside the main pane (CompanyNav /
  // BrainNav). The Settings area (no contour of its own) must also NOT show
  // the board-picker tree (2026-06-21 bugfix: it used to leak in because the
  // URL→contour fallback is 'operational').
  const showSidebar =
    contour !== 'company' &&
    contour !== 'brain' &&
    contour !== 'files' &&
    contour !== 'comms' &&
    // Market owns its whole pane (storefront grid, pack card, checkout); the
    // board-picker tree beside it would be navigation for a different area.
    contour !== 'market' &&
    // The University owns its whole pane (catalogue grid, session diff,
    // experiment cards); the board-picker tree beside it would be navigation
    // for a different area, exactly as it would be beside Market.
    contour !== 'learning' &&
    !inSettings;

  const drawerCtxValue = React.useMemo(
    () => ({ open: drawerOpen, setOpen: setDrawerOpen }),
    [drawerOpen],
  );

  // ────────── Desktop layout (md and up) ──────────
  if (!isMobile) {
    return (
      <MobileDrawerContext.Provider value={drawerCtxValue}>
        <div className="relative flex h-dvh w-full overflow-hidden bg-background">
          <ActivityBar
            active={contour}
            onSelect={onContourSelect}
            canAdminOrg={canAdminOrg}
            ceoUserId={ceoUserId}
          />
          {showSidebar && (
            // Plan A — Company ▸ Project ▸ Board grouped tree (all orgs);
            // the gear lives in the bottom-left profile menu inside it.
            // 2026-06-18: chevrons removed. The column opens/closes via the
            // Boards rail icon; the resize handle snap-collapses when the
            // user over-drags past the min width. `onCollapse` lets the
            // in-panel resize handle flip the shell-level collapsed state.
            <React.Suspense fallback={null}>
              <CompanyTree
                collapsed={collapsed}
                onToggle={toggle}
                onCollapse={() => setCollapsedPersist(true)}
              />
            </React.Suspense>
          )}
          {/* Settings-V2 (2026-06-18): no chevron. When the boards column is
              collapsed, the rail's right edge becomes a thin drag handle —
              dragging it outward re-EXPANDS the column to its last width. */}
          {showSidebar && collapsed && (
            <SidebarExpandEdge onExpand={() => setCollapsedPersist(false)} />
          )}
          {/* Settings-V2 (2026-06-19, founder spec): the slim top utility
              strip that the 2026-06-18 commit added to host the Conductor
              was reverted — a full-width row for a single icon wasted
              vertical space. <main> is once again a single scroll area for
              BOTH flag states, so the OFF path stays byte-identical and the
              ON path reclaims the strip's height. The Conductor now lives in
              the existing board status line (board/$boardId.lazy.tsx, to the
              right of the RAM/CPU/Storage panel), gated under the flag. */}
          <main className="flex-1 overflow-y-auto">
            <LegacyBrowserBanner />
            <ClaudeAuthBanner />
            <ExecutionLaneBanner />
            {children}
          </main>
        </div>
      </MobileDrawerContext.Provider>
    );
  }

  // ────────── Mobile layout ──────────
  // The main content fills the viewport. Bottom nav is fixed-positioned.
  // The left rail lives behind a drawer triggered from the top bar.
  return (
    <MobileDrawerContext.Provider value={drawerCtxValue}>
      <div className="relative flex h-dvh w-full flex-col overflow-hidden bg-background">
        {/* Top bar — SHELL-level since 2026-08-01 (QA D-1/D-2/D-3). It carries
            the burger (the only way into the drawer) and the scope switcher,
            so it must exist on every route, not just the two that used to
            mount it themselves. <ScopeBar> renders null on desktop, so the
            desktop branch above deliberately does not mount it. */}
        <ScopeBar />
        <main className="flex-1 overflow-y-auto pb-bottom-nav">
          <LegacyBrowserBanner />
          <ClaudeAuthBanner />
          <ExecutionLaneBanner />
          {children}
        </main>
        <BottomNav />

        {/* Off-canvas left drawer */}
        <div
          aria-hidden={!drawerOpen}
          className={cn(
            'fixed inset-0 z-50 transition-opacity duration-200',
            drawerOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            tabIndex={drawerOpen ? 0 : -1}
            className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer panel. Founder spec 2026-05-25 (Wave-D burger
              refactor): drop the ActivityBar — Boards/Files/Company
              already live in the bottom nav, so duplicating them in
              the drawer is noise. Sidebar renders in flat-mobile mode
              so projects render without chevrons and each board gets
              its own 3-dot menu (Board Rules / Trash). */}
          <aside
            role="dialog"
            aria-modal={drawerOpen}
            aria-label="Navigation menu"
            className={cn(
              'absolute inset-y-0 left-0 flex h-full w-[86%] max-w-[340px] flex-col bg-background shadow-2xl transition-transform duration-300 ease-out',
              'pt-safe pb-safe pl-safe',
              drawerOpen ? 'translate-x-0' : '-translate-x-full',
            )}
          >
            <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
              <span className="text-[13px] font-semibold tracking-tight text-foreground">
                Menu
              </span>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* Wave-E (2026-06-10): the duplicate primary-nav block
                  (Boards/Inbox/Files/Company/Settings) was removed — it
                  mirrored the bottom bar one-for-one. Settings now lives
                  in the (scrollable) bottom bar, so every contour is
                  reachable there. The drawer is now just the project /
                  board tree. */}
              {showSidebar ? (
                <React.Suspense fallback={null}>
                  <CompanyTree collapsed={false} onToggle={() => undefined} variant="flat-mobile" />
                </React.Suspense>
              ) : (
                <div className="p-4 text-sm text-muted-foreground">
                  {contour === 'brain'
                    ? 'Brain tools are in the main area.'
                    : 'Company tools are in the main area.'}
                </div>
              )}
            </div>
            {/* Account row — OUTSIDE the `showSidebar` branch on purpose
                (2026-08-01). It used to be the CompanyTree's own footer, which
                meant Settings and Logout simply did not exist on the contours
                that render no tree (Files, Brain, Company, Comms, Settings).
                Pinned below the scroll area so it survives a long board tree. */}
            <MobileAccountFooter />
          </aside>
        </div>
      </div>
    </MobileDrawerContext.Provider>
  );
}
